import {
  type CreateWorkshopAppointmentDto,
  type FindAllWorkshopAppointmentsQuery,
  type UpdateWorkshopAppointmentDto,
  type WorkshopAppointment,
} from '@/entities/workshop-appointments/workshop-appointment.entity';
import type { MigrateBlockerToWorkshopAppointmentDto } from '@carvia/ros-client-types';
import { isCarUnavailableError } from '@/entities/api-error';
import { workshopAppointmentsService } from '@/api/workshop-appointments.service';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';

const baseKey = ['workshopAppointments'];

const catchCarUnavailableError = (error: unknown) => {
  if (isCarUnavailableError(error)) {
    (error as { __handled?: boolean }).__handled = true;
  }
  throw error;
};

export const useWorkshopAppointments = (
  query: MaybeRefOrGetter<FindAllWorkshopAppointmentsQuery>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: computed(() => [...baseKey, toValue(query)]),
    queryFn: ({ pageParam = 1 }) =>
      workshopAppointmentsService.getAllWithQuery({
        ...toValue(query),
        page: pageParam,
        limit: 20,
      }),
    enabled,
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useWorkshopAppointment = (
  id: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: computed(() => [...baseKey, toValue(id)]),
    queryFn: () => workshopAppointmentsService.getOne(toValue(id)),
    enabled,
  });
};

export const useWorkshopAppointmentTotalCosts = (
  carId: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: computed(() => [...baseKey, 'totalCosts', toValue(carId)]),
    queryFn: () => workshopAppointmentsService.getTotalCostsByCarId(toValue(carId)),
    enabled: computed(() => Boolean(toValue(carId)) && Boolean(toValue(enabled))),
  });
};

export const useMigrateBlockerToWorkshopAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...baseKey, 'migrateFromBlocker'],
    mutationFn: ({
      allocationId,
      dto,
    }: {
      allocationId: string;
      dto: MigrateBlockerToWorkshopAppointmentDto;
    }) => workshopAppointmentsService.migrateFromBlocker(allocationId, dto),
    onMutate: () => ({
      successMessage: 'Workshop appointment created successfully',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: baseKey });
      queryClient.invalidateQueries({ queryKey: ['allocations'] });
    },
  });
};

export const useCreateWorkshopAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: baseKey,
    mutationFn: (dto: CreateWorkshopAppointmentDto) =>
      workshopAppointmentsService.create(dto).catch(catchCarUnavailableError),
    onMutate: () => ({
      successMessage: 'Workshop appointment created successfully',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: baseKey });
    },
  });
};

export const useUpdateWorkshopAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: baseKey,
    mutationFn: ({ id, dto }: { id: string; dto: UpdateWorkshopAppointmentDto }) =>
      workshopAppointmentsService.update(id, dto).catch(catchCarUnavailableError),
    onMutate: () => ({
      successMessage: 'Workshop appointment updated successfully',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result: WorkshopAppointment, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: baseKey });
    },
  });
};

export const useDeleteWorkshopAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: baseKey,
    mutationFn: (id: string) => workshopAppointmentsService.remove(id),
    onMutate: () => ({
      successMessage: 'Workshop appointment deleted successfully',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: baseKey });
    },
  });
};
